import gql from 'graphql-tag';
import { useQuery } from 'urql';

import { useQueryParams } from '@/hooks/useQueryParams';
import { mapVariables } from '@/utils';
import { makeOrderBy, makePagination } from '@/utils/pagination';
import {
  WelcomeOffersQuery,
  WelcomeOffersQueryVariables,
} from './__generated__/useWelcomeOffers';
import { queryParams } from './queryParams';

const WELCOME_OFFERS_QUERY = gql`
  query WelcomeOffers(
    $first: Int
    $last: Int
    $after: String
    $before: String
    $desc: Boolean
    $brand: BrandEnum
    $depositNumber: Int
    $country: ISOCountryCode
    $currency: ISOCurrencyCode
    $affiliateId: String
  ) {
    viewer {
      id
      brands {
        name
        code
      }
      welcomeOffers(
        first: $first
        last: $last
        after: $after
        before: $before
        desc: $desc
        brand: $brand
        depositNumber: $depositNumber
        country: $country
        currency: $currency
        affiliateId: $affiliateId
      ) {
        edges {
          node {
            id
            brand {
              name
              code
            }
            name
            currency
            countries
            affiliateId
            depositNumber
            expirationDate
            modifiedBy
            modifiedAt
            version
            rewardTemplates {
              ... on FreespinsTemplate {
                __typename
                spreadOverDays
                gameProvider
                gameId
                staticAmount
                dynamicAmount {
                  maxDepositAmount
                  multiplier
                  maxRewardAmount
                }
                valueCents
                payLines
              }
              ... on BonusMoneyTemplate {
                __typename
                staticAmount
                dynamicAmount {
                  maxDepositAmount
                  multiplier
                  maxRewardAmount
                }
                wageringRequirementFactor
                staticMaxPayout
                dynamicMaxPayoutMultiplier
              }
              ... on MoneyTemplate {
                __typename
                staticAmount
                dynamicAmount {
                  maxDepositAmount
                  multiplier
                  maxRewardAmount
                }
              }
              ... on SportsbookMoneyTemplate {
                __typename
                staticAmount
                dynamicAmount {
                  maxDepositAmount
                  multiplier
                  maxRewardAmount
                }
              }
            }
          }
        }
        pageInfo {
          endCursor
          hasNextPage
          startCursor
          hasPreviousPage
        }
      }
    }
  }
`;

export default function useWelcomeOffers() {
  const [query, setQuery] = useQueryParams(queryParams);

  const [{ data, fetching }, refresh] = useQuery<
    WelcomeOffersQuery,
    WelcomeOffersQueryVariables
  >({
    query: WELCOME_OFFERS_QUERY,
    variables: {
      ...mapVariables(query),
      depositNumber:
        query.depositNumber != null && query.depositNumber !== ''
          ? Number(query.depositNumber)
          : undefined,
    },
  });

  return {
    refresh,
    fetching,
    defaultFilter: query,
    brands: data?.viewer?.brands || [],
    updateFilter: (values: {}) => setQuery(values, 'replaceIn'),
    ...makeOrderBy(query, setQuery),
    ...makePagination(query, setQuery, data?.viewer.welcomeOffers.pageInfo),
    welcomeOffers: data?.viewer.welcomeOffers?.edges?.map((edge) => edge?.node),
  };
}
